import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import camelcaseKeys from 'camelcase-keys'
import { fetchMe, Me } from '@/services/AuthService'
import { clearToken, getToken } from '@/services/token'

type AuthState = {
  me: Me | null
  loading: boolean
  isAuthenticated: boolean
  refresh: () => Promise<void>
  logout: () => void
}

const Ctx = createContext<AuthState>({
  me: null,
  loading: false,
  isAuthenticated: false,
  refresh: async () => {},
  logout: () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(!!getToken())

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const user = await fetchMe()
      setMe(camelcaseKeys(user))
    } catch {
      setMe(null)
      clearToken() // clear invalid/stale token so guards can redirect
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    setMe(null)
    clearToken()
  }, [])

  // On first load: if token exists, fetch user
  useEffect(() => {
    if (getToken()) {
      void refresh()
    }
  }, [refresh])

  // Memoize the context value so consumers don't re-render on every
  // AuthProvider re-render (previously value={{...}} inline recreated
  // the object each pass, cascading through the whole tree).
  const value = useMemo<AuthState>(() => ({
    me,
    loading,
    isAuthenticated: !!me,
    refresh,
    logout,
  }), [me, loading, refresh, logout])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export const useAuth = () => useContext(Ctx)
